import { Button, Tooltip } from '@chakra-ui/react'
import { addHours } from 'date-fns'
import { useAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import iconCloseXGray100 from '~/assets/icons-light-theme/icon-close-x-gray100.svg'
import { useAuthenticationState } from '~/atoms/AuthenticationState'
import { StorageKeys } from '~/atoms/StorageKeys'
import { Text } from '~/components/core'
import { ImageIcon } from '~/components/ImageIcon'
import { light } from '~/constants/theme'
import { useClientUpdateAvailableCheck } from '~/hooks/useCheckClientVersion'
import { usePhoneEndpoint } from '~/hooks/usePhone'
import { BannerProps } from '~/pages/Home/Banners/BannerManager'
import { useElectronUpdaterContext } from '~/providers/ElectronUpdaterContext'
import { isElectron, isLocalBuildEnvironment } from '~/utils/common'

interface WrapperProps {
  show: boolean
}

const Wrapper = styled.div<WrapperProps>`
  height: ${({ show }) => (show ? '56' : '0')}px;
  padding: 0 16px;
  display: flex;
  width: 100%;
  justify-content: space-between;
  background-color: ${light.green100};
  align-items: center;
  overflow: hidden;
  transition: all 200ms ease;
`
const TextWrapper = styled.div`
  display: flex;
  align-items: center;
`
interface AppVersionOutOfDateBannerState {
  lastDismissedDate?: Date | undefined
  lastDismissedVersion?: string | undefined
}

const versionOutOfDateBannerStateAtom =
  atomWithStorage<AppVersionOutOfDateBannerState>(
    StorageKeys.AppVersionBanner,
    {},
  )

const AppVersionOutOfDateBanner = ({ onShowCallback }: BannerProps) => {
  const [show, setShow] = useState(false)
  const { lastChecked, currentVersion, latestVersion, isClientOutOfDate } =
    useClientUpdateAvailableCheck()
  const [versionOutOfDateBannerState, setVersionOutOfDateBannerState] = useAtom(
    versionOutOfDateBannerStateAtom,
  )
  const { availableVersion, isUpdateDownloaded, quitAndInstall } =
    useElectronUpdaterContext()

  const {
    authenticationState: { validationToken, session },
  } = useAuthenticationState()
  const {
    state: {
      phoneCalls: {
        list: { callList },
      },
    },
  } = usePhoneEndpoint({})

  const shouldShowBanner = useCallback((): boolean => {
    if (isElectron) {
      return isUpdateDownloaded
    } else {
      if (!isClientOutOfDate) return false
      if (!versionOutOfDateBannerState.lastDismissedDate) return true

      // Consider moving date parsing outside the check if possible
      const lastDismissedDate = new Date(
        versionOutOfDateBannerState.lastDismissedDate,
      )
      const lastDismissedVersion =
        versionOutOfDateBannerState.lastDismissedVersion

      return (
        addHours(lastDismissedDate, 1) < new Date() ||
        lastDismissedVersion !== latestVersion
      )
    }
  }, [
    isElectron,
    isClientOutOfDate,
    versionOutOfDateBannerState.lastDismissedDate,
    versionOutOfDateBannerState.lastDismissedVersion,
    latestVersion,
    isUpdateDownloaded,
  ])

  const updateVersionOutOfDateBannerState = useCallback(() => {
    setVersionOutOfDateBannerState({
      lastDismissedDate: new Date(),
      lastDismissedVersion: latestVersion,
    })
  }, [setVersionOutOfDateBannerState, latestVersion])

  const handleSetShow = (val: boolean) => {
    setShow(val)
    updateVersionOutOfDateBannerState()
    onShowCallback(val)
  }

  const tempHideBanner = useMemo(() => {
    return callList.size > 0
  }, [callList])

  useEffect(() => {
    if (!isElectron && !show && shouldShowBanner() && isClientOutOfDate) {
      window.gtag('event', 'user_shown_version_banner', {
        latestVersion,
        currentVersion,
        user: session?.user.username,
        userId: session?.user.id,
        extensions: validationToken?.data ?? 'Unknown',
      })

      handleSetShow(isClientOutOfDate)
    }
  }, [
    show,
    lastChecked,
    isClientOutOfDate,
    latestVersion,
    currentVersion,
    session,
    validationToken,
    shouldShowBanner,
  ])

  useEffect(() => {
    if (isElectron && !show && shouldShowBanner() && isUpdateDownloaded) {
      handleSetShow(isClientOutOfDate)
    }
  }, [show, shouldShowBanner, isUpdateDownloaded])

  useEffect(() => {
    if (!show) {
      return
    }
    const shouldShowNow = !tempHideBanner && show
    onShowCallback(shouldShowNow)
  }, [tempHideBanner, show, onShowCallback])

  useEffect(() => {
    if (isElectron && isUpdateDownloaded) {
      onShowCallback(true)
      setShow(true)
    }
  }, [isUpdateDownloaded])

  const dismissBanner = useCallback(() => {
    window.gtag('event', 'user_dismissed_version_banner', {
      latestVersion,
      currentVersion,
      user: session?.user.username,
      userId: session?.user.id,
      extensions: validationToken?.data ?? 'Unknown',
    })
    handleSetShow(false)
  }, [handleSetShow, latestVersion, currentVersion, session, validationToken])

  const handleRefreshClicked = useCallback(() => {
    window.gtag('event', 'user_refreshed_from_version_banner', {
      latestVersion,
      currentVersion,
      user: session?.user.username,
      userId: session?.user.id,
      extensions: validationToken?.data ?? 'Unknown',
    })
    window.location.reload()
  }, [latestVersion, currentVersion, session, validationToken])

  const willShowBanner =
    !tempHideBanner &&
    ((!isElectron && !isLocalBuildEnvironment() && show) ||
      (isElectron && isUpdateDownloaded && show))

  return (
    <Wrapper show={willShowBanner}>
      <TextWrapper>
        <Text color={light.gray100}>
          {isElectron
            ? `Version ${availableVersion} of the Mango Desktop App is available!`
            : 'A new version of the Mango Web App is available!'}
        </Text>
        <Button
          marginLeft={'.5rem'}
          onClick={isElectron ? quitAndInstall : handleRefreshClicked}
          bg="gray.100"
          borderRadius="full"
          _hover={{ bg: 'green.100' }}
          size="sm"
        >
          {isElectron ? 'Restart & Update' : 'Update Now'}
        </Button>
      </TextWrapper>
      <Tooltip label={'Remind me later'} placement={'left'}>
        <div style={{ cursor: 'pointer', marginLeft: '0.5rem' }}>
          <ImageIcon
            title="Dismiss"
            onClick={() => dismissBanner()}
            width="20"
            height="20"
            icon={iconCloseXGray100}
          />
        </div>
      </Tooltip>
    </Wrapper>
  )
}

export default AppVersionOutOfDateBanner
